"use client";

import type { FileIcon } from "@untitledui/file-icons";
import { FileIcon as FileTypeIcon } from "@untitledui/file-icons";
import {
	CheckCircle,
	Trash01,
	UploadCloud02,
	XCircle,
} from "@untitledui/icons";
import { AnimatePresence, motion } from "motion/react";
import type { ComponentProps, ComponentPropsWithRef } from "react";
import { useId, useRef, useState } from "react";
import { cx } from "@/utils/cx";
import { Button } from "../button/button-untitledui";
import { ButtonUtility } from "../button-utility";
import { FeaturedIcon } from "../featured-icon";
import { ProgressBar } from "../progress-indicators";

/**
 * Returns a human-readable file size.
 * @param bytes - The size of the file in bytes.
 * @returns A string representing the file size in a human-readable format.
 */
export const getReadableFileSize = (bytes: number) => {
	if (bytes === 0) {
		return "0 KB";
	}

	const suffixes = ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

	const i = Math.floor(Math.log(bytes) / Math.log(1024));

	return `${Math.floor(bytes / 1024 ** i)} ${suffixes[i]}`;
};

type FileUploadDropZoneProps = {
	/** The class name of the drop zone. */
	className?: string;
	/**
	 * A hint text explaining what files can be dropped.
	 */
	hint?: string;
	/**
	 * Disables dropping or uploading files.
	 */
	isDisabled?: boolean;
	/**
	 * Specifies the types of files that the server accepts.
	 * Examples: "image/*", ".pdf,image/*", "image/*,video/mpeg,application/pdf"
	 */
	accept?: string;
	/**
	 * Allows multiple file uploads.
	 */
	allowsMultiple?: boolean;
	/**
	 * Maximum file size in bytes.
	 */
	maxSize?: number;
	/**
	 * Callback function that is called with the list of dropped files
	 * when files are dropped on the drop zone.
	 */
	onDropFiles?: (files: FileList) => void;
	/**
	 * Callback function that is called with the list of unaccepted files
	 * when files are dropped on the drop zone.
	 */
	onDropUnacceptedFiles?: (files: FileList) => void;
	/**
	 * Callback function that is called with the list of files that exceed
	 * the size limit when files are dropped on the drop zone.
	 */
	onSizeLimitExceed?: (files: FileList) => void;
};

export const FileUploadDropZone = ({
	className,
	hint,
	isDisabled,
	accept,
	allowsMultiple = true,
	maxSize,
	onDropFiles,
	onDropUnacceptedFiles,
	onSizeLimitExceed,
}: FileUploadDropZoneProps) => {
	const id = useId();
	const inputRef = useRef<HTMLInputElement>(null);
	const [isInvalid, setIsInvalid] = useState(false);
	const [isDraggingOver, setIsDraggingOver] = useState(false);

	const isFileTypeAccepted = (file: File): boolean => {
		if (!accept) {
			return true;
		}

		// Split the accept string into individual types
		const acceptedTypes = accept.split(",").map((type) => type.trim());

		return acceptedTypes.some((acceptedType) => {
			// Handle file extensions (e.g., .pdf, .doc)
			if (acceptedType.startsWith(".")) {
				const extension = `.${file.name.split(".").pop()?.toLowerCase()}`;
				return extension === acceptedType.toLowerCase();
			}

			// Handle wildcards (e.g., image/*)
			if (acceptedType.endsWith("/*")) {
				const typePrefix = acceptedType.split("/")[0];
				return file.type.startsWith(`${typePrefix}/`);
			}

			// Handle exact MIME types (e.g., application/pdf)
			return file.type === acceptedType;
		});
	};

	const handleDragIn = (event: React.DragEvent<HTMLDivElement>) => {
		if (isDisabled) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
		setIsDraggingOver(true);
	};

	const handleDragOut = (event: React.DragEvent<HTMLDivElement>) => {
		if (isDisabled) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
		setIsDraggingOver(false);
	};

	const processFiles = (files: File[]): void => {
		// Reset the invalid state when processing files.
		setIsInvalid(false);

		const acceptedFiles: File[] = [];
		const unacceptedFiles: File[] = [];
		const oversizedFiles: File[] = [];

		// If multiple files are not allowed, only process the first file
		const filesToProcess = allowsMultiple ? files : files.slice(0, 1);

		for (const file of filesToProcess) {
			// Check file size first
			if (maxSize && file.size > maxSize) {
				oversizedFiles.push(file);
				// não fazemos return aqui; continuamos para classificar os demais arquivos
				continue;
			}

			// Then check file type
			if (isFileTypeAccepted(file)) {
				acceptedFiles.push(file);
			} else {
				unacceptedFiles.push(file);
			}
		}

		// Handle oversized files
		if (oversizedFiles.length > 0 && typeof onSizeLimitExceed === "function") {
			const dataTransfer = new DataTransfer();
			for (const file of oversizedFiles) {
				dataTransfer.items.add(file);
			}

			setIsInvalid(true);
			onSizeLimitExceed(dataTransfer.files);
		}

		// Handle accepted files
		if (acceptedFiles.length > 0 && typeof onDropFiles === "function") {
			const dataTransfer = new DataTransfer();
			for (const file of acceptedFiles) {
				dataTransfer.items.add(file);
			}
			onDropFiles(dataTransfer.files);
		}

		// Handle unaccepted files
		if (
			unacceptedFiles.length > 0 &&
			typeof onDropUnacceptedFiles === "function"
		) {
			const unacceptedDataTransfer = new DataTransfer();
			for (const file of unacceptedFiles) {
				unacceptedDataTransfer.items.add(file);
			}

			setIsInvalid(true);
			onDropUnacceptedFiles(unacceptedDataTransfer.files);
		}

		// Clear the input value to ensure the same file can be selected again
		if (inputRef.current) {
			inputRef.current.value = "";
		}
	};

	const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
		if (isDisabled) {
			return;
		}

		handleDragOut(event);
		processFiles(Array.from(event.dataTransfer.files));
	};

	const handleInputFileChange = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		processFiles(Array.from(event.target.files || []));
	};

	return (
		<div
			className={cx(
				"relative flex max-w-full flex-col items-center gap-3 rounded-xl bg-primary px-6 py-4 text-tertiary ring-1 ring-secondary ring-inset transition duration-100 ease-linear",
				isDraggingOver && "ring-2 ring-brand",
				isDisabled &&
					"cursor-not-allowed bg-disabled_subtle ring-disabled_subtle",
				className
			)}
			data-dropzone
			onDragEnd={handleDragOut}
			onDragEnter={handleDragIn}
			onDragLeave={handleDragOut}
			onDragOver={handleDragIn}
			onDrop={handleDrop}
		>
			<FeaturedIcon color="gray" size="md" theme="modern">
				<UploadCloud02 className="size-5" />
			</FeaturedIcon>

			<div className="flex max-w-full flex-col gap-1 text-center">
				<div className="flex max-w-full justify-center gap-1 text-center">
					<input
						accept={accept}
						className="peer sr-only"
						disabled={isDisabled}
						id={id}
						multiple={allowsMultiple}
						onChange={handleInputFileChange}
						ref={inputRef}
						type="file"
					/>
					<label className="flex max-w-full cursor-pointer" htmlFor={id}>
						<Button
							className="w-full max-w-full whitespace-break-spaces"
							color="link-color"
							isDisabled={isDisabled}
							onClick={() => inputRef.current?.click()}
							size="md"
						>
							Clique para fazer o upload{" "}
							<span className="md:hidden">e anexar arquivos</span>
						</Button>
					</label>
					<span className="text-sm max-md:hidden">ou arraste e solte</span>
				</div>
				<p
					className={cx(
						"whitespace-pre-line text-xs transition duration-100 ease-linear",
						!!isInvalid && "text-error-primary"
					)}
				>
					{hint || "SVG, PNG, JPG or GIF (max. 800x400px)"}
				</p>
			</div>
		</div>
	);
};

export type FileListItemProps = {
	/** The name of the file. */
	name: string;
	/** The size of the file. */
	size: number;
	/** The upload progress of the file. */
	progress: number;
	/** Whether the file failed to upload. */
	failed?: boolean;
	/** The type of the file. */
	type?: ComponentProps<typeof FileIcon>["type"];
	/** The class name of the file list item. */
	className?: string;
	/** The variant of the file icon. */
	fileIconVariant?: ComponentProps<typeof FileTypeIcon>["variant"];
	/** The function to call when the file is deleted. */
	onDelete?: () => void;
	/** The function to call when the file upload is retried. */
	onRetry?: () => void;
};

export const FileListItemProgressBar = ({
	name,
	size,
	progress,
	failed,
	type,
	fileIconVariant,
	onDelete,
	onRetry,
	className,
}: FileListItemProps) => {
	const isComplete = progress === 100;

	return (
		<motion.li
			className={cx(
				"relative flex gap-3 rounded-xl bg-primary p-4 ring-1 ring-secondary ring-inset transition-shadow duration-100 ease-linear",
				!!failed && "ring-2 ring-error",
				className
			)}
			layout="position"
		>
			<FileTypeIcon
				className="size-10 shrink-0 dark:hidden"
				theme="light"
				type={type ?? "empty"}
				variant={fileIconVariant ?? "default"}
			/>
			<FileTypeIcon
				className="not-dark:hidden size-10 shrink-0"
				theme="dark"
				type={type ?? "empty"}
				variant={fileIconVariant ?? "default"}
			/>

			<div className="flex min-w-0 flex-1 flex-col items-start">
				<div className="flex w-full min-w-0 max-w-full flex-1">
					<div className="min-w-0 flex-1">
						<p className="truncate font-medium text-secondary text-sm">
							{name}
						</p>

						<div className="mt-0.5 flex items-center gap-2">
							{!!size && (
								<>
									<p className="truncate whitespace-nowrap text-sm text-tertiary">
										{getReadableFileSize(size)}
									</p>
									<hr className="h-3 w-px rounded-t-full rounded-b-full border-none bg-border-primary" />
								</>
							)}

							<div className="flex items-center gap-1">
								{!!isComplete && (
									<CheckCircle className="size-4 stroke-[2.5px] text-fg-success-primary" />
								)}
								{!!isComplete && (
									<p className="font-medium text-sm text-success-primary">
										Completo
									</p>
								)}

								{!(isComplete || failed) && (
									<UploadCloud02 className="size-4 stroke-[2.5px text-fg-quaternary" />
								)}
								{!(isComplete || failed) && (
									<p className="font-medium text-quaternary text-sm">
										Uploading...
									</p>
								)}

								{!!failed && (
									<XCircle className="size-4 text-fg-error-primary" />
								)}
								{!!failed && (
									<p className="font-medium text-error-primary text-sm">
										Failed
									</p>
								)}
							</div>
						</div>
					</div>

					<ButtonUtility
						className="-mt-2 -mr-2 self-start"
						color="tertiary"
						icon={Trash01}
						onClick={onDelete}
						size="xs"
						tooltip="Delete"
					/>
				</div>

				{!failed && (
					<div className="mt-1 w-full">
						<ProgressBar
							labelPosition="right"
							max={100}
							min={0}
							value={progress}
						/>
					</div>
				)}

				{!!failed && (
					<Button
						className="mt-1.5"
						color="link-destructive"
						onClick={onRetry}
						size="sm"
					>
						Try again
					</Button>
				)}
			</div>
		</motion.li>
	);
};

export const FileListItemProgressFill = ({
	name,
	size,
	progress,
	failed,
	type,
	fileIconVariant,
	onDelete,
	onRetry,
	className,
}: FileListItemProps) => {
	const isComplete = progress === 100;

	return (
		<motion.li
			className={cx(
				"relative flex gap-3 overflow-hidden rounded-xl bg-primary p-4",
				className
			)}
			layout="position"
		>
			{/* Progress fill. */}
			<div
				aria-valuemax={100}
				aria-valuemin={0}
				aria-valuenow={progress}
				className={cx(
					"absolute inset-0 size-full bg-secondary transition duration-75 ease-linear",
					!!isComplete && "opacity-0"
				)}
				role="progressbar"
				style={{ transform: `translateX(-${100 - progress}%)` }}
			/>
			{/* Inner ring. */}
			<div
				className={cx(
					"absolute inset-0 size-full rounded-[inherit] ring-1 ring-secondary ring-inset transition duration-100 ease-linear",
					!!failed && "ring-2 ring-error"
				)}
			/>
			<FileTypeIcon
				className="relative size-10 shrink-0 dark:hidden"
				theme="light"
				type={type ?? "empty"}
				variant={fileIconVariant ?? "solid"}
			/>
			<FileTypeIcon
				className="relative not-dark:hidden size-10 shrink-0"
				theme="dark"
				type={type ?? "empty"}
				variant={fileIconVariant ?? "solid"}
			/>

			<div className="relative flex min-w-0 flex-1">
				<div className="relative flex min-w-0 flex-1 flex-col items-start">
					<div className="w-full min-w-0 flex-1">
						<p className="truncate font-medium text-secondary text-sm">
							{name}
						</p>

						<div className="mt-0.5 flex items-center gap-2">
							<p className="text-sm text-tertiary">
								{failed
									? "Upload failed, please try again"
									: getReadableFileSize(size)}
							</p>

							{!failed && (
								<>
									<hr className="h-3 w-px rounded-t-full rounded-b-full border-none bg-border-primary" />
									<div className="flex items-center gap-1">
										{!!isComplete && (
											<CheckCircle className="size-4 stroke-[2.5px] text-fg-success-primary" />
										)}
										{!isComplete && (
											<UploadCloud02 className="size-4 stroke-[2.5px] text-fg-quaternary" />
										)}

										<p className="text-sm text-tertiary">{progress}%</p>
									</div>
								</>
							)}
						</div>
					</div>

					{!!failed && (
						<Button
							className="mt-1.5"
							color="link-destructive"
							onClick={onRetry}
							size="sm"
						>
							Try again
						</Button>
					)}
				</div>

				<ButtonUtility
					className="-mt-2 -mr-2 self-start"
					color="tertiary"
					icon={Trash01}
					onClick={onDelete}
					size="xs"
					tooltip="Delete"
				/>
			</div>
		</motion.li>
	);
};

const FileUploadRoot = (props: ComponentPropsWithRef<"div">) => (
	<div
		{...props}
		className={cx("flex max-w-full flex-col gap-4", props.className)}
	>
		{props.children}
	</div>
);

const FileUploadList = (props: ComponentPropsWithRef<"ul">) => (
	<ul {...props} className={cx("flex flex-col gap-3", props.className)}>
		<AnimatePresence initial={false}>{props.children}</AnimatePresence>
	</ul>
);

export const FileUpload = {
	Root: FileUploadRoot,
	List: FileUploadList,
	DropZone: FileUploadDropZone,
	ListItemProgressBar: FileListItemProgressBar,
	ListItemProgressFill: FileListItemProgressFill,
};
